import React, { useState, useEffect } from 'react';
import API from '../../pages/Api';

const TableContainer = ({
    columns,            
    data = [],          
    apiUrl,             
    renderRow,          
    searchQuery = '',   
    setSearchQuery,    
    typeQuery = '',   
    setTypeQuery,    
    searchPlaceholder = "Type to search globally across all pages...",
    customActions = null
}) => {
    const [serverData, setServerData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    

    const isServerPaginated = !!apiUrl && !(
        apiUrl.includes('getAllAmounts') || 
        apiUrl.includes('getAllVideoAndStudyList')
    );
    // const isServerPaginated = false;
    const formatCurrency = (val) => {
        const num = parseFloat(val);
        return isNaN(num) ? "0.00" : num.toFixed(2);
    };

    const formatDollar = (val) => {
        const num = parseFloat(val);
        return isNaN(num) ? "0.00" : num.toFixed(2);
    };

    const fetchServerData = async () => {
        
        if (!isServerPaginated) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            
            const res = await API.get(`${apiUrl}?search=${searchQuery}&page=${currentPage}&type=${typeQuery?typeQuery:'All'}`, { headers });
            console.log(res);
            
            if (res.data.results) {
                setServerData(res.data.results);
                setTotalRecords(res.data.count || res.data.results.length);
            } else {
                setServerData(res.data);
                setTotalRecords(res.data.length);
            }
        } catch (err) {
            console.error("Centralized inner pipeline execution fetch failed:", err);
            setServerData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isServerPaginated) {
            const delayDebounceFn = setTimeout(() => {
                fetchServerData();
            }, 400);
            return () => clearTimeout(delayDebounceFn);
        }
    }, [searchQuery, currentPage, apiUrl]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const clientFilteredData = isServerPaginated 
        ? [] 
        : (Array.isArray(data) ? data : []).filter(item => {
            const description = item.AmtDesc || item.titledesc || '';
            return description.toLowerCase().includes(searchQuery.toLowerCase());
        });

    const displayData = isServerPaginated 
        ? serverData 
        : clientFilteredData.slice((currentPage - 1) * 10, currentPage * 10);

    const totalRecordsCount = isServerPaginated 
        ? totalRecords 
        : clientFilteredData.length;

    const totalPagesCount = Math.ceil(totalRecordsCount / 10) || 1;

    const handlePrev = () => {
        if (currentPage > 1) setCurrentPage(prev => prev - 1);
    };

    const handleNext = () => {
        if (currentPage < totalPagesCount) setCurrentPage(prev => prev + 1);
    };

    const getPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= totalPagesCount; i++) pages.push(i);
        return pages;
    };

    return (
        <div className="space-y-4 w-full animate-fadeIn">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full">
                {setSearchQuery && (
                    <div className="flex items-center gap-2 w-full"> {/* 💡 flex लावला */}
                        <div className="w-full sm:max-w-md bg-white p-2 rounded border border-gray-300 shadow-sm flex items-center gap-3">
                            <span className="text-xs font-bold text-gray-600 uppercase pl-2">Search:</span>
                            <input 
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={searchPlaceholder}
                                className="flex-1 p-1.5 border border-gray-300 rounded text-xs outline-none bg-gray-50 font-semibold text-gray-700"
                            />
                        </div>
                        
                        {customActions && <div className="ml-auto mr-2">{customActions}</div>}
                    </div>
                )}
            </div>
            <div className="bg-white rounded shadow-sm border border-gray-300 w-full flex flex-col">
                <div
                    className="overflow-x-auto w-full"
                    style={{
                        WebkitOverflowScrolling: "touch"
                    }}
                >
                    <div className="md:hidden text-xs text-gray-500 px-2 py-1">
                        ← Swipe left/right to view table →
                    </div>
                    <table className="w-[900px] md:w-full text-[12px] border-collapse text-center table-auto">
                        <thead className="bg-[#032167] text-white font-bold uppercase select-none">
                            <tr className="border-b border-gray-300">
                                <th className="py-3 px-2 border-r w-[8%] tracking-wider">Sr. No.</th>
                                {columns.map((col, idx) => (
                                    <th key={idx} className="p-2 border-r last:border-none tracking-wider text-center">{col}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="text-gray-700 font-medium">
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length + 1} className="p-10 text-center text-gray-400 uppercase italic font-bold">
                                        Extracting real-time database elements...
                                    </td>
                                </tr>
                            ) : displayData && displayData.length > 0 ? (
                                displayData.map((item, index) => {
                                    const computedSerialNo = (currentPage - 1) * 10 + (index + 1);
                                    
                                    return renderRow(item, index, computedSerialNo, formatCurrency, formatDollar, isServerPaginated ? fetchServerData : null);
                                })
                            ) : (
                                <tr>
                                    <td colSpan={columns.length + 1} className="p-10 text-center text-gray-400 uppercase italic font-bold">
                                        No matched system tracking logs verified.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center p-3 bg-gray-50 border-t border-gray-200 gap-3 select-none">
                    <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
                        Showing {totalRecordsCount > 0 ? ((currentPage - 1) * 10 + 1) : 0} to {Math.min(currentPage * 10, totalRecordsCount)} of {totalRecordsCount} Records
                    </span>
                    
                    <div className="flex items-center gap-1.5">
                        <button type="button" disabled={currentPage === 1 || loading} onClick={handlePrev} className="px-3 py-1.5 bg-white border border-gray-300 rounded text-[11px] font-bold uppercase text-gray-700 hover:bg-gray-100 disabled:opacity-40">Previous</button>
                        <div className="hidden md:flex items-center gap-1">
                            {getPageNumbers().map((pageNo) => (
                                <button key={pageNo} type="button" onClick={() => setCurrentPage(pageNo)} className={`w-7 h-7 rounded text-[11px] font-bold border ${currentPage === pageNo ? 'bg-[#032167] text-white' : 'bg-white'}`}>
                                    {pageNo}
                                </button>
                            ))}
                        </div>
                        <button type="button" disabled={currentPage === totalPagesCount || loading} onClick={handleNext} className="px-3 py-1.5 bg-white border border-gray-300 rounded text-[11px] font-bold uppercase text-gray-700 hover:bg-gray-100 disabled:opacity-40">Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TableContainer;